/**
 * useBootstrapAuth (US-AE.F9-0100 BUG-1) — khôi phục phiên lúc app khởi động.
 *
 * Token access giữ IN-MEMORY (mất khi reload). Nếu còn refresh cookie httpOnly hợp lệ, một lần
 * silent `refreshToken()` lúc boot sẽ đặt lại access token → hard-reload / deep-link KHÔNG bị đá về
 * /login oan. Thất bại (không cookie / hết hạn) → trả về không token; RequireAuth xử lý redirect
 * như cũ (đúng cho phiên thật sự đã hết).
 *
 * Trả `ready=false` tới khi refresh xong (một lần) để tránh nháy /login trước khi token về.
 */
import { useEffect, useState } from "react";
import { refreshToken, scheduleTokenRefresh } from "@/lib/auth.js";

/** Trên trang /login KHÔNG có phiên để khôi phục → bỏ qua refresh (tránh 401/nhiễu + phí quota). */
function isLoginRoute(): boolean {
  return typeof window !== "undefined" && window.location.pathname.startsWith("/login");
}

/**
 * Trần thời gian chờ boot-refresh (ms). Nếu refresh không resolve trong ngưỡng này (API treo/chậm,
 * KHÔNG phải lỗi cứng), vẫn cho `ready=true` để KHÔNG kẹt màn chờ vĩnh viễn — RequireAuth xử lý tiếp
 * (token in-memory nếu có, else /login). Chống app-boot-hang (review HIGH).
 */
const BOOT_REFRESH_TIMEOUT_MS = 5_000;

export function useBootstrapAuth(): { ready: boolean } {
  // /login: sẵn sàng ngay, không refresh (không có refresh cookie hợp lệ để đổi).
  const [ready, setReady] = useState(() => isLoginRoute());

  useEffect(() => {
    if (isLoginRoute()) return;
    let active = true;
    const markReady = () => {
      if (active) setReady(true);
    };
    // Trần thời gian CHỈ chống app-boot-hang (API treo). Nó không phải đường thoát xác thực:
    // authedFetch tự refresh trước khi gửi nếu token chưa về, nên không còn request trần ăn 401.
    const timer = setTimeout(markReady, BOOT_REFRESH_TIMEOUT_MS);
    void refreshToken().then((token) => {
      clearTimeout(timer);
      // Khôi phục được phiên → nối lịch refresh chủ động theo `exp`, để tab mở lâu hơn TTL (10m)
      // KHÔNG rơi vào 401 "Unauthorized" (trước đây chỉ refresh đúng một lần lúc boot).
      if (token) scheduleTokenRefresh();
      markReady();
    });
    // KHÔNG huỷ lịch refresh ở cleanup: vòng đời của nó là VÒNG ĐỜI PHIÊN, không phải của component
    // (App mount một lần; StrictMode dev còn unmount/remount → cleanup sẽ giết nhầm lịch vừa đặt).
    // Nơi huỷ đúng là logout() — đã gọi cancelScheduledRefresh().
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, []);

  return { ready };
}
